import { NgSignaturePadOptions, SignaturePadComponent } from '@almothafar/angular-signature-pad';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { StampDateTimeHelper } from 'src/app/_helpers/stampDateTimeHelper';
import { SignatureTabEnum } from 'src/app/constants/signatureTabs.enum';
import { RequestModel } from 'src/app/models/requests';
import { Signinglocations } from 'src/app/models/signinglocations';
import { Signingreasons } from 'src/app/models/signingreasons';
import { User } from 'src/app/models/user';
import { EngineApiClient } from 'src/app/services/api-clients/engine.api-client';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { LoggingService } from 'src/app/services/logging.service';
import { OrganisationApiClient } from 'src/app/services/organisation.apiclient';
import { PkisigningService } from 'src/app/services/pkisigning.service';
import { ToastService } from 'src/app/services/toast.service';
import { ViewerDataService } from '../../viewer-data.service';

@Component({
  templateUrl: './perform-action-sign.modal.html'
})
export class PerformActionSignModal implements OnInit {

  @Input() requestName!: string;

  @Input('owner') owner!: string;
  @Input('request') request!: RequestModel;
  @Input('user') user!: User;
  @Input('eSeal') eSeal: boolean = false;
  @Input('showLocation') showLocation: boolean = false;
  @Input('showReason') showReason: boolean = false;
  @Input('showStamp') showStamp: boolean = true;
  @Input('showDraw') showDraw: boolean = true;
  @Input('showPicture') showPicture: boolean = true;
  @Input('signatureProviderName') signatureProviderName!: any;
  @Input('signatureProviders') signatureProviders!: any;
  @Output('sign') sign = new EventEmitter();
  @Output('changeSigningMethod') changeSigningMethod = new EventEmitter();
  @Output('newSignMethod') newSignMethod = new EventEmitter();
  @Output('signsession') signsession = new EventEmitter();
  /**
   *  THE PRE SELECTED SIGNATURE PROVIDER
   *  If the user does NOT pick a new one this holds the Signature provider
   */
  @Input('signatureProvider') signatureProvider!: any;
  /**
   *  THE SELECTED SIGNATURE PROVIDER
   *  If the user does pick a new one this holds the Signature provider
   *  The original (signatureProvider) is not changed in this form
   */
  selectedSignatureProvider!: any;

  @ViewChild('signature')
  public signaturePad: SignaturePadComponent | undefined;
  activeTab: SignatureTabEnum = SignatureTabEnum.DigitalStamp;
  form: UntypedFormGroup;
  translations: any = <any>{};
  stampImage!: string;
  stampTextImage!: string;
  scribbleText: boolean = false;
  imageTextAdded: boolean = false;
  changeMethod: boolean = false;
  signatureUploaded: boolean = false;
  uploadedSigningImage!: string;
  signingImage = new Image();
  reasons: Signingreasons[] = [];
  locations: Signinglocations[] = [];
  reason!: string;
  location!: string;
  changeSignMethod: boolean = false;
  protected buttonsEnabled: boolean = true
  protected zoomLevel: number = localStorage.getItem('SigningImageZoomLevel') != null ? parseInt(localStorage.getItem('SigningImageZoomLevel')!) : 50;
  protected canvasWidth: number = 435;
  protected canvasHeight: number = 145;
  protected signaturePadOptions: NgSignaturePadOptions = {
    canvasWidth: this.canvasWidth,
    canvasHeight: this.canvasHeight
  };

  scribbleCanvas: HTMLCanvasElement | null = null;
  imageCanvas: HTMLCanvasElement | null = null;
  /**
   * Actor number, to display what task you are on
   */
  expectedActionNumber = 1;
  constructor(private formBuilder: UntypedFormBuilder, public activeModal: NgbActiveModal, private toastService: ToastService, private organisationApiClient: OrganisationApiClient, private translateService: TranslateService, private stampDateTimeHelper: StampDateTimeHelper,
    private pkisigning: PkisigningService, private loggingService: LoggingService, private sanitizer: DomSanitizer, private engineApiClient: EngineApiClient, private localStorageService: LocalStorageService, private viewerDataService: ViewerDataService) {
    // Must not be empty. It's approved by a comment.
    this.viewerDataService.getCurrentActor$().subscribe(currentActor => {
      this.expectedActionNumber = currentActor.badgeNumber;
    });

    this.form = this.formBuilder.group(
      {
        reason: new UntypedFormControl(''),
        location: new UntypedFormControl('')
      }
    );
    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
  }

  async ngOnInit(): Promise<void> {
    this.getSignatureProviders();
    if (this.showStamp) {
      this.getStamp().catch((error) => this.loggingService.logException(error));
    }
    if (this.showPicture) {
      await this.getUploadedImageWhenPicureTabIsActive();
    }
    this.setActiveTab()


    if (this.user?.isCustomer && this.showReason) {
      this.getSigningReasons();
    }
    if (this.user?.isCustomer && this.showLocation) {
      this.getSigningLocations();
    }
  }

  private setActiveTab() {
    if (this.showStamp) {
      this.activeTab = SignatureTabEnum.DigitalStamp;
    }
    else if (!this.showStamp && this.showDraw) {
      this.activeTab = SignatureTabEnum.MouseOrTouch;
    }
    else if (!this.showStamp && !this.showDraw && this.showPicture) {
      this.activeTab = SignatureTabEnum.UploadPicture;
    }
  }

  getTranslations() {
    this.translateService.get([
      "Dialog.ElectronicallySignedBy",
      "Dialog.ElectronicSignatureCompliesPart1",
      "Dialog.ElectronicSignatureCompliesPart2",
      "Dialog.ElectronicSignatureCompliesPart3",
      'Toasts.ImageSizeToBig',
      'Toasts.InvalidImageType',
      'Toasts.GetLocationError',
      'Toasts.GetReasonError',
      'Toasts.Error'
    ]).subscribe(translation => {
      this.translations.ElectronicallySignedBy = translation['Dialog.ElectronicallySignedBy'];
      this.translations.ElectronicSignatureCompliesPart1 = translation['Dialog.ElectronicSignatureCompliesPart1'];
      this.translations.ElectronicSignatureCompliesPart2 = translation['Dialog.ElectronicSignatureCompliesPart2'];
      this.translations.ElectronicSignatureCompliesPart3 = translation['Dialog.ElectronicSignatureCompliesPart3'];
      this.translations.toastsImageSizeToBig = translation['Toasts.ImageSizeToBig'];
      this.translations.toastsInvalidImageType = translation['Toasts.InvalidImageType'];
      this.translations.toastsGetLocationError = translation['Toasts.GetLocationError'];
      this.translations.toastsGetReasonError = translation['Toasts.GetReasonError'];
      this.translations.error = translation['Toasts.Error'];
    });
  }

  getNewSignMethod(event: any) {
    this.selectedSignatureProvider = event;
    this.newSignMethod.emit(event);
  }

  back() {
    this.signsession.emit("close");
  }

  drawStart(event: MouseEvent | Touch) {
    this.signaturePad?.set("penColor", "#4169E1");
    document.getElementById("SignatureInfo")?.classList.add('invisible');
  }

  private getSigningLocations() {
    this.organisationApiClient.getSigningLocations().subscribe({
      next: (data: any) => {
        this.locations = data;
      },
      error: () => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsGetLocationError + "");
      }
    });
  }

  private getSigningReasons() {
    this.organisationApiClient.getSigningReasons().subscribe({
      next: (data: any) => {
        this.reasons = data;
      },
      error: () => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsGetReasonError + "");
      }
    });
  }

  startSignflow() {
    this.buttonsEnabled = false;
    let signature: string = "";
    if (this.activeTab === "MouseOrTouch") {
      this.scribbleCanvas = <HTMLCanvasElement>document.querySelector(".cnvSignature canvas");
      let result = this.removeSignatureWhitespace(this.signaturePad!.toDataURL('image/svg+xml'), this.scribbleCanvas);
      signature = result.svg;
    } else if (this.activeTab === "UploadPicture") {
      signature = this.localStorageService.signingImage!;
    }
    (<HTMLInputElement>document.getElementById("signBtn")).disabled = true;
    let signEvent: any = {
      activeTab: this.activeTab,
      signature: signature,
      embedTimestampInSvg: this.scribbleText,
      reason: this.reason,
      location: this.location,
    };

    // pre open for external accounts since this code is linked to a button click popup blocker acceps it (since its linked to use interaction
    // only do this IF: \
    // the newly selected provider (selectedSignatureProvider) have requiresExternalAccount
    // or the preselected provider (signatureProvider) has requiresExternalAccount, AND no new provider WAS selected/Set
    if (this.selectedSignatureProvider?.requiresExternalAccount || (!this.selectedSignatureProvider && this.signatureProvider?.requiresExternalAccount)) {
      this.pkisigning.preOpenCscPopup();
    }
    // with the new vidua filter, it can show vidua, but not have it selected, in that case we CAN just in time selection a provider
    if (!this.selectedSignatureProvider && this.signatureProvider) {
      // SPECIFIC CASE FOR VIDUA
      if (this.signatureProvider.parameters && this.signatureProvider.parameters[0] && this.signatureProvider.parameters[0] == 'cleverbase') {
        this.newSignMethod.emit(this.signatureProvider);
      }
    }
    this.sign.emit(signEvent);
  }

  private async getStamp() {
    let y = this;
    let stamp = this.organisationApiClient.getStamp(600, 200);
    let lastValueStamp = await lastValueFrom(stamp);
    let reader = new FileReader();
    reader.readAsDataURL(lastValueStamp);
    reader.onloadend = function () {
      y.stampImage = reader.result!.toString();
    }
  }

  public async getUploadedImageWhenPicureTabIsActive() {
    if (this.localStorageService.hasSigningImage) {
      this.signatureUploaded = true;
    }
    this.imageCanvas = <HTMLCanvasElement>document.getElementById("signatureImageCanvas");
    if (!this.imageCanvas) return;
    await this.getUploadedImageWhenUploaded();
  }

  private getUploadedImageWhenUploaded() {
    if (!this.localStorageService.hasSigningImage) return;
    this.uploadedSigningImage = this.localStorageService.signingImage!;
    let y = this;
    this.signingImage.src = this.uploadedSigningImage;
    this.signingImage.onload = function () {
      let ctx = y.imageCanvas!.getContext("2d");
      ctx!.drawImage(y.signingImage, (y.imageCanvas!.width / 2) - (y.signingImage.width / 2), (y.imageCanvas!.height / 2) - (y.signingImage.height / 2), y.signingImage.width, y.signingImage.height);
      y.setSignatureByTextStamp();
    }
  }

  public removeUploadedImage() {
    this.localStorageService.signingImage = null;
    this.signatureUploaded = false;
    this.uploadedSigningImage = '';
    this.scribbleText = false;
    this.imageTextAdded = false;
  }

  public getScribble() {
    this.scribbleCanvas = <HTMLCanvasElement>document.querySelector(".cnvSignature canvas");
    this.setTextInScribbleImageWhenSwitchedChecked();
  }

  private getSignatureProviders() {
    this.pkisigning.getSignatureProviders();
  }

  uploadSigningImage(event: any) {
    let y = this;
    let reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);
    let name = (event.target.files[0].name).toLowerCase();
    let lastDot = name.lastIndexOf('.');
    let ext = name.substring(lastDot + 1);
    if ((event.target.files[0].size / 1024) < 100) {
      if (ext == 'png' || ext == 'jpg' || ext == 'jpeg' || ext == 'gif' || ext == 'bmp') {
        reader.onload = function () {
          y.localStorageService.signingImage = reader.result!.toString()!;
          y.signatureUploaded = true;
        };
        reader.onloadend = function () {
          y.getUploadedImageWhenPicureTabIsActive();
        };
      } else {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsInvalidImageType + "");
      }
    } else {
      this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsImageSizeToBig + "");
    }
  }

  async setSignatureByTextStamp() {
    if (this.scribbleText) {
      let ctx: CanvasRenderingContext2D | null = null;
      if (this.activeTab === SignatureTabEnum.MouseOrTouch) {
        ctx = this.scribbleCanvas!.getContext("2d");
      } else if (this.activeTab === SignatureTabEnum.UploadPicture) {
        ctx = this.imageCanvas!.getContext("2d");
      }
      if (ctx === null) return;

      let y = this;
      let stampText = this.engineApiClient.getStamp(600, 200, true);
      let lastValueStamp = await lastValueFrom(stampText);
      let reader = new FileReader();
      reader.readAsDataURL(lastValueStamp);
      reader.onloadend = function () {
        y.stampTextImage = reader.result!.toString();
        let image = new Image();
        image.src = y.stampTextImage;
        image.width = y.canvasWidth;
        image.height = y.canvasHeight;
        setTimeout(() => {
          if (y.activeTab === SignatureTabEnum.MouseOrTouch) {
            ctx!.drawImage(image, 10, 10, y.scribbleCanvas!.width, y.scribbleCanvas!.height);
          } else if (y.activeTab === SignatureTabEnum.UploadPicture) {
            ctx!.drawImage(image, 10, 10, y.imageCanvas!.width, y.imageCanvas!.height);
          }
          ctx!.save();
        }, 500);
      }
    }
  }

  clearScribble() {
    this.signaturePad?.clear();
    this.scribbleText = false;
  }

  async setTextInScribbleImageWhenSwitchedChecked() {
    if (this.scribbleText) {
      if (this.activeTab === SignatureTabEnum.MouseOrTouch) {
        if (!document.getElementById("SignatureInfo")?.classList.contains('invisible')) document.getElementById("SignatureInfo")?.classList.add('invisible');
        await this.setSignatureByTextStamp();
      } else if (this.activeTab === SignatureTabEnum.UploadPicture) {
        this.getUploadedImageWhenPicureTabIsActive();
      }
    } else {
      if (this.activeTab === SignatureTabEnum.MouseOrTouch) {
        if (document.getElementById("SignatureInfo")?.classList.contains('invisible')) document.getElementById("SignatureInfo")?.classList.remove('invisible');
        this.signaturePad?.clear();
      } else if (this.activeTab === SignatureTabEnum.UploadPicture) {
        let ctx: CanvasRenderingContext2D | null = this.imageCanvas!.getContext("2d");
        if (ctx !== null) ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.getUploadedImageWhenPicureTabIsActive();
      }
    }
  }

  private removeSignatureWhitespace(orgsvg: string, canvas: HTMLCanvasElement) {
    let croppedCanvas: HTMLCanvasElement = document.createElement('canvas');
    let croppedCtx: CanvasRenderingContext2D = croppedCanvas.getContext("2d")!;

    croppedCanvas.width = canvas.width;
    croppedCanvas.height = canvas.height;
    let w: number = croppedCanvas.width;
    let h: number = croppedCanvas.height;
    croppedCtx.drawImage(canvas, 0, 0, w, h);

    let pix: any = { x: [], y: [] };
    let imageData = croppedCtx.getImageData(0, 0, w, h);
    let index = 0;

    for (let y: number = 0; y < h; y++) {
      for (let x: number = 0; x < w; x++) {
        index = (y * w + x) * 4;

        if (imageData.data[index + 3] > 0) {
          pix.x.push(x);
          pix.y.push(y);
        }
      }
    }

    pix.x.sort((a: number, b: number) => a - b);
    pix.y.sort((a: number, b: number) => a - b);
    // let n = pix.x.length - 1;

    // w = pix.x[n] - pix.x[0];
    // h = pix.y[n] - pix.y[0];

    let cut = croppedCtx.getImageData(pix.x[0], pix.y[0], w, h);

    // croppedCanvas.width = w;
    // croppedCanvas.height = h;
    croppedCtx.putImageData(cut, 0, 0);

    let ratio = Math.max(window.devicePixelRatio || 1, 1);
    croppedCtx.scale(ratio, ratio);

    let svgurl = orgsvg.split(',');
    let svg = atob(svgurl[1]);
    ratio = 1;
    svg = svg.replace(/viewBox="[^"]+"/,
      'viewBox="' + pix.x[0] / ratio + ' ' + pix.y[0] / ratio + ' ' + w / ratio + ' ' + h / ratio + '"');

    svgurl[1] = btoa(svg);
    return { svg: svgurl.join(','), w: w, h: h };
  }

  disableSignButton() {
    if (!this.buttonsEnabled) {
      return true;
    } else if (this.activeTab === "MouseOrTouch") {
      if (this.signaturePad?.isEmpty()) return true;
      else return false;
    } else if (this.activeTab === "UploadPicture") {
      return !this.localStorageService.hasSigningImage;
    } else {
      return false;
    }
  }

  fieldChange(key: string, event: any): boolean {
    const formField = document.getElementById(key);

    if (!formField) return true;

    if (this.form.controls[key].invalid) {
      formField.classList.add('is-invalid');
      return false;
    }

    formField.classList.remove('is-invalid');
    if (key === "reason") this.reason = this.form.controls[key].value;
    if (key === "location") this.location = this.form.controls[key].value;
    return true;
  }

  setReason(reason: string) {
    this.form.controls['reason'].setValue(reason);
    this.reason = reason;
  }

  setLocation(location: string) {
    this.form.controls['location'].setValue(location);
    this.location = location;
  }
}
